import { useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import { makeFormStyles } from '../styles';
import SetDirtyForm from 'forms/SetDirtyForm';
import { FormActions } from 'helpers/form-action';
import { FormTextField } from 'components/TextField';
import { closePanel } from 'features/common/formSlice';
import { snackbarHandler } from 'GeminiViewerComponent/helpers/snackbar-handler';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import { createCategory, fetchCategoryById, updateCategory } from 'features/category/categorySlice';
import {
    setProgress,
    activateLoading,
    deactivateLoading,
} from 'GeminiViewerComponent/features/globals/loadingProgressSlice';

const INIT_VALUES = {
    display_name: '',
    asset_type_id: 0,
};

const CategoryForm = ({ formAction, categoryId }) => {
    //#region Constants
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    //#endregion Constants

    //#region Hooks
    //#endregion Hooks

    //#region State
    const [loaded, setLoaded] = useState(formAction.id === FormActions.Create.id ? true : false);
    const [initValues, setInitValues] = useState({ ...INIT_VALUES });
    //#endregion State

    //#region Selectors
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);
    //#endregion Selectors

    //#region Refs
    const categoryNameRef = useRef(null);
    //#endregion Refs

    //#region Effects
    useEffect(() => {
        if (formAction.id === FormActions.Edit.id) {
            const fetchData = async () => {
                let objectData = await dispatch(fetchCategoryById(categoryId));
                let fetchedObject = objectData.payload;
                setInitValues({
                    ...fetchedObject,
                });
                setLoaded(true);
            };
            fetchData();
        } else {
            setInitValues({ display_name: '' });
        }
    }, [categoryId]); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion Effects

    //#region Methods
    const handleSubmit = async (values, setSubmitting, resetForm) => {
        dispatch(activateLoading({ showProgress: true }));
        let resultAction = null;

        switch (formAction.id) {
            case FormActions.Edit.id:
                resultAction = await dispatch(
                    updateCategory({
                        ...values,
                    })
                );
                if (!resultAction.error) {
                    dispatch(closePanel({ formKey: 'categoryForm' }));
                }
                break;
            case FormActions.Create.id:
                dispatch(
                    setProgress({
                        progress: 0,
                        label: 'Step 1 of 2: Uploading',
                        noAnimation: true,
                    })
                );
                resultAction = await dispatch(createCategory({ ...values }));
                if (!resultAction.error) {
                    setInitValues({ ...INIT_VALUES });
                    resetForm();
                    if (categoryNameRef.current) {
                        categoryNameRef.current.focus();
                    }
                }
                break;
            default:
                break;
        }
        if (resultAction?.error) {
            enqueueSnackbar(resultAction?.error?.message, {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
        } else {
            const { message, variant } = snackbarHandler(resultAction.meta.requestStatus, formAction.label);
            enqueueSnackbar(message, {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: variant,
            });
        }
        dispatch(deactivateLoading());
        setSubmitting(false);
    };
    //#endregion Methods

    //#region Render time calcs
    //#endregion Render time calcs

    //#region Render
    return !loaded ? (
        <LoadingLogo styleClassName={classes.loadingSvg} />
    ) : (
        <Formik
            enableReinitialize={true}
            initialValues={{ ...initValues }}
            validationSchema={Yup.object({
                display_name: Yup.string().max(100, 'Must be 100 characters or less').required('Required'),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                handleSubmit(values, setSubmitting, resetForm);
            }}
        >
            {({ isSubmitting, values, setFieldValue }) => (
                <Form className={classes.form}>
                    <div className={classes.formHeader}>Category Details</div>
                    <FormTextField
                        inputRef={categoryNameRef}
                        autoFocus
                        label="Category Name"
                        name="display_name"
                        placeholder="Category Name"
                    />
                    <FormControl style={{ margin: '5px 0' }} fullWidth>
                        <InputLabel id="category_type_label">Category Type</InputLabel>
                        <Select
                            labelId="category_type"
                            id="category_type"
                            name="asset_type_id"
                            value={values.asset_type_id || 0}
                            label="Display Mode"
                            onChange={(e) => {
                                setFieldValue('asset_type_id', e.target.value);
                            }}
                        >
                            <MenuItem key={1} value={1}>
                                Assets Only
                            </MenuItem>
                            <MenuItem key={3} value={3}>
                                Procedures Only
                            </MenuItem>
                            <MenuItem key={0} value={0}>
                                All
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <SetDirtyForm />
                    <button className={classes.submit} type="submit" disabled={isSubmitting}>
                        {formAction.buttonLabel}
                    </button>
                </Form>
            )}
        </Formik>
    );
    //#endregion Render
};

export { CategoryForm };
